// Fiók-űrlap (full-page, create + edit közös) — react-hook-form + a @atlas/contracts writable mezői (zodResolver
// a features/accounts/form.ts sémájával). A form-INSTANCE-t a route birtokolja (a 422 → mező-hiba a mutációból
// form.setError-rel írható); ez a komponens prezentációs: a szekciókat és a mentés-gombot rendeli. A select-mezők
// opcióit az Odin reference-data (account-refs) adja, „nincs" sentinellel + a jelenlegi (nem-vokabulált) érték
// megőrzésével.
//
// SZEKCIÓK, NEM KÁRTYÁK (user-döntés — az ügyfél-oldal mintája): a lap EGY balra igazított hasáb
// `FormSection`-ökkel (se keret, se háttér, `overline` cím), nem `Card`-ok rácsa. A sorrend a régi CRM
// tab-sorrendje egy görgethető lapra kiterítve: Alapadatok · Egyéb adatok · Szolgáltatások · Számlázás ·
// Integrációk · Kapcsolattartók · Ügyféladminisztrátorok (`features/accounts/sections.ts`).
//
// A MEZŐ-CSOPORTOSÍTÁS a legacy szerkesztő tabjait követi, hogy a napi használó ne keresgéljen:
//   • Alapadatok — mi ez a fiók és hol van: név · weboldal · típus/besorolás/státusz · régió/szobaszám · cím,
//   • Egyéb adatok — a technikai kiszolgáló-rendszerek + domainek + jegyzet (legacy „other_data"),
//   • Számlázás — a számlázási profil FELÜLÍRÁSA (legacy „override_billing" + profil-választó),
//   • Integrációk — az Asana-név és a külső rendszer-azonosítók LISTÁJA (HotelCMS / ZadirCMS / MailWizz,
//     rendszerenként több is, megjegyzéssel — `AccountIntegrationsField`).
//
// EGY FORM, EGY PATCH, EGY MENTÉS-GOMB (a legacy is egyetlen `wire:submit="save"` volt): a négy írható szekció
// UGYANANNAK a resource-nak a mezői, tehát szekciónkénti külön mentés azt a csapdát nyitná, hogy a másik
// szekcióban tett módosítás NÉMÁN elveszik. A gomb ezért az UTOLSÓ írható szekció (Integrációk) alján áll —
// ami NEM a „lap alja": utána még három szekció következik (Kapcsolattartók · Ügyféladminisztrátorok ·
// Veszélyzóna), amiket nem ez a gomb ment.
import { type ClientBillingProfileSchema } from '@atlas/contracts';
import { CLIENT_STATUSES } from '@atlas/shared';
import { type TFunction } from 'i18next';
import { type ReactNode } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { type z } from 'zod';

import { EntityCombobox, type ComboboxOption } from '@/components/shared/entity-combobox';
import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import { PillField, SelectField, TextField, TextareaField } from '@/components/shared/form-fields';
import { FormSection } from '@/components/shared/form-section';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form } from '@/components/ui/form';
import { Text } from '@/components/ui/text';
import { AccountDomainsField } from '@/features/accounts/components/account-domains-field';
import { AccountIntegrationsField } from '@/features/accounts/components/account-integrations-field';
import { ACCOUNT_SELECT_NONE, type AccountFormValues } from '@/features/accounts/form';
import { ACCOUNT_SECTION } from '@/features/accounts/sections';
import { CLIENT_STATUS_META } from '@/features/clients/status';

/** Az Odin reference-data szótárai (slug → hivatalos név) — az account-refs végpontról. */
export interface AccountRefs {
  type: Record<string, string>;
  pms: Record<string, string>;
  bookingEngine: Record<string, string>;
  channelManager: Record<string, string>;
  hosting: Record<string, string>;
  /** Régió KÓD → hivatalos név. */
  region: Record<string, string>;
  // ── LEGACY-PARITÁS szótárak (v4): a legacy JSON-tömb oszlopok zárt értékkészletei. Opcionálisak, mert a
  //    régebbi `/account-refs` válaszok még nem hordozták őket (a `multiOptions` üres szótárt is elbír).
  exportCampaign?: Record<string, string>;
  smtp?: Record<string, string>;
  mailing?: Record<string, string>;
}

type ClientBillingProfile = z.infer<typeof ClientBillingProfileSchema>;

/** A szülő-ügyfél választó (CSAK create-módban) — kontrollált érték + async keresés + előtöltés/zárolás. */
export interface AccountClientPicker {
  value: string | null;
  onChange: (id: string | null) => void;
  search: (q: string) => Promise<ComboboxOption[]>;
  initialOption?: ComboboxOption | null;
  /** `?clientId=`-ből érkezett (az ügyfél-detail „Fiók hozzáadása") → előtöltve + zárolva. */
  locked?: boolean;
}

/** A rögzített besorolás-skála (a legacy AccountCategoryEnum) — nincs reference-data mögötte. */
const CATEGORY_VALUES = ['A++', 'A+', 'A', 'B', 'C'];

interface AccountFormProps {
  form: UseFormReturn<AccountFormValues>;
  mode: 'create' | 'edit';
  refs: AccountRefs | undefined;
  /** A szülő-ügyfél számlázási profiljai a választóhoz (edit: a detailből; create: a kiválasztott ügyfélből). */
  billingProfileOptions?: ClientBillingProfile[];
  /** CREATE-mód szülő-ügyfél választó (edit-módban nincs — a fiók szülője fix). */
  clientPicker?: AccountClientPicker;
  /** View-only (nincs accounts.manage): a mezők disabled, az Integrációk + a mentés-gomb rejtve. */
  disabled?: boolean;
  pending?: boolean;
  onSubmit: (values: AccountFormValues) => void;
  t: TFunction;
  /**
   * A „Szolgáltatások" szekció — a kért sorrend szerint az Egyéb adatok UTÁN, a Számlázás ELŐTT áll, tehát a
   * form KÖZEPÉN. Ezért külön slot és nem `children`: a fiók szolgáltatásai read-only lista (a hozzárendelés a
   * Szolgáltatások modulban történik), a form-mezők közé beszúrva.
   */
  servicesSlot?: ReactNode;
  /**
   * A státusz-pirulák ALATT megjelenő figyelmeztetés — ma az INAKTIVÁLÁS számlázási következménye
   * (`DeactivationBillingNotice`). A hívó adja: az adat (a fiók nyitott számlázás-sorai) a detail-oldalé.
   */
  statusNotice?: ReactNode;
  /** További szekciók a mentés-gomb UTÁN (Kapcsolattartók · Ügyféladminisztrátorok — create-en zárolva). */
  children?: ReactNode;
}

export function AccountForm({
  form,
  mode,
  refs,
  billingProfileOptions = [],
  clientPicker,
  disabled = false,
  pending = false,
  onSubmit,
  t,
  servicesSlot,
  statusNotice,
  children,
}: AccountFormProps): JSX.Element {
  const statusOptions = CLIENT_STATUSES.map((s) => {
    const Icon = CLIENT_STATUS_META[s].icon;
    return {
      value: s,
      label: t(`accounts.statusLabel.${s}`, { defaultValue: s }),
      icon: <Icon className="h-4 w-4" />,
      activeClassName: CLIENT_STATUS_META[s].pillActiveClass,
    };
  });

  // Select-opciók egy reference-map-ból: „— nincs —" sentinel elöl + a vokabulárium + a jelenlegi (nem
  // vokabulált) érték megőrzése (hogy a legacy slug ne tűnjön el a szerkesztőben).
  const options = (
    map: Record<string, string> | undefined,
    fieldValue: string,
  ): { value: string; label: string }[] => {
    const entries = Object.entries(map ?? {}).map(([value, label]) => ({ value, label }));
    if (
      fieldValue &&
      fieldValue !== ACCOUNT_SELECT_NONE &&
      !entries.some((e) => e.value === fieldValue)
    ) {
      entries.push({ value: fieldValue, label: fieldValue });
    }
    entries.sort((a, b) => a.label.localeCompare(b.label, 'hu'));
    return [{ value: ACCOUNT_SELECT_NONE, label: t('accounts.form.none') }, ...entries];
  };

  const categoryOptions = [
    { value: ACCOUNT_SELECT_NONE, label: t('accounts.form.none') },
    ...CATEGORY_VALUES.map((v) => ({ value: v, label: v })),
  ];

  const v = form.getValues();
  const selectedTypes = form.watch('types');
  const selectedSmtps = form.watch('smtps');
  const selectedMailings = form.watch('mailings');
  const selectedCampaigns = form.watch('exportCampaigns');

  /**
   * Reference-szótár (KÓD → név) → több-választó opciók, névre rendezve. A nem-vokabulált, de MÁR KIVÁLASZTOTT
   * értéket megőrizzük (a legacy slug ne tűnjön el a szerkesztésen) — a `typeMultiOptions` bevált mintája.
   */
  const multiOptions = (
    dict: Record<string, string> | undefined,
    selected: string[],
  ): { id: string; name: string }[] => {
    const entries = Object.entries(dict ?? {}).map(([id, name]) => ({ id, name }));
    const known = new Set(entries.map((e) => e.id));
    for (const val of selected) if (!known.has(val)) entries.push({ id: val, name: val });
    entries.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
    return entries;
  };

  // A több-választós Típus/kategória opciói a `type` szótárból (KÓD → név); a nem-vokabulált, de már kiválasztott
  // értékeket megőrizzük (a legacy slug ne tűnjön el), névre rendezve.
  const typeMultiOptions = (() => {
    const entries = Object.entries(refs?.type ?? {}).map(([id, name]) => ({ id, name }));
    const known = new Set(entries.map((e) => e.id));
    for (const val of selectedTypes) {
      if (!known.has(val)) entries.push({ id: val, name: val });
    }
    entries.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
    return entries;
  })();

  // Számlázási-profil opciók: NONE sentinel („ügyfél elsődlegese") + a profilok (cégnév · adószám · (elsődleges)).
  const billingProfileSelectOptions = [
    { value: ACCOUNT_SELECT_NONE, label: t('accounts.form.billingProfileNone') },
    ...billingProfileOptions.map((p) => {
      const parts = [p.billingName];
      if (p.taxNumber) parts.push(p.taxNumber);
      const suffix = p.isPrimary ? ` ${t('accounts.form.billingProfilePrimary')}` : '';
      return { value: p.id, label: `${parts.join(' · ')}${suffix}` };
    }),
  ];

  // CREATE-nél a szülő-ügyfél kiválasztásáig a számlázási-profil választó tiltott (nincs mihez kötni).
  const parentChosen = mode === 'edit' || Boolean(clientPicker?.value);
  // AUTOMATIKUS SZÁMLÁZÁS ZÁRVA, ha a fiók az ÜGYFÉL számlázását használja (nincs saját felülírás), és az ügyfélnek
  // nincs elsődleges profilja — a szerver ezt 422-vel utasítaná el (mért eset: így nem lehetett fiókot felvenni).
  // A már bekapcsolt jelölő kikapcsolható marad. Az ügyfél-űrlap ugyanígy zárja a saját kapcsolóját.
  const autoBillingBlocked =
    parentChosen &&
    !form.watch('overrideBilling') &&
    !form.watch('autoBilling') &&
    !billingProfileOptions.some((p) => p.isPrimary);
  const autoBillingError = form.formState.errors.autoBilling?.message;
  const billingProfileDisabled = disabled || !parentChosen;
  const submitDisabled = pending || (mode === 'create' && !clientPicker?.value);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* Szülő-ügyfél (CSAK create) — kötelező; a fiók ehhez az ügyfélhez tartozik, utólag nem módosítható. */}
        {mode === 'create' && clientPicker && (
          <FormSection id={ACCOUNT_SECTION.parent} title={t('accounts.form.sectionParent')}>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('accounts.form.parentClient')}
                <span className="text-destructive"> *</span>
              </label>
              <EntityCombobox
                value={clientPicker.value}
                onChange={clientPicker.onChange}
                search={clientPicker.search}
                initialOption={clientPicker.initialOption ?? null}
                placeholder={t('accounts.form.parentClientPlaceholder')}
                disabled={disabled || clientPicker.locked}
                clearable={!clientPicker.locked}
                aria-label={t('accounts.form.parentClient')}
              />
              <p className="text-subtle text-xs">{t('accounts.form.parentClientHint')}</p>
            </div>
          </FormSection>
        )}

        {/* ALAPADATOK — mi ez a fiók (név · weboldal · típus/besorolás/státusz · régió/szobaszám) és hol van
            (cím). A legacy „base_data" tabja: a cím ott is ide tartozott, ezért nincs külön „Cím" szekció. */}
        <FormSection id={ACCOUNT_SECTION.basics} title={t('accounts.sections.basics')}>
          <div className="space-y-5">
            <TextField<AccountFormValues>
              name="name"
              label={t('accounts.form.name')}
              required
              disabled={disabled}
              autoComplete="off"
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField<AccountFormValues>
                name="website"
                label={t('accounts.form.website')}
                disabled={disabled}
                autoComplete="off"
              />
              {/* Több-választós Típus/kategória (a régi single `type` helyett). */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('accounts.form.types')}</label>
                <EntityMultiSelect
                  options={typeMultiOptions}
                  value={selectedTypes}
                  onChange={(ids) => form.setValue('types', ids, { shouldDirty: true })}
                  placeholder={t('accounts.form.none')}
                  disabled={disabled}
                  aria-label={t('accounts.form.types')}
                />
              </div>
              <SelectField<AccountFormValues>
                name="category"
                label={t('accounts.form.category')}
                disabled={disabled}
                placeholder={t('accounts.form.none')}
                options={categoryOptions}
              />
              <SelectField<AccountFormValues>
                name="region"
                label={t('accounts.form.region')}
                disabled={disabled}
                placeholder={t('accounts.form.none')}
                options={options(refs?.region, v.region)}
              />
              <TextField<AccountFormValues>
                name="roomCount"
                label={t('accounts.form.roomCount')}
                disabled={disabled}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
            <PillField<AccountFormValues>
              name="status"
              label={t('accounts.form.status')}
              disabled={disabled}
              options={statusOptions}
            />
            {statusNotice}
            {/* Cím — a fiók FIZIKAI helye (a szálloda/telephely), nem a számlázási cím: az a számlázási
                profilon él. Ezért az alapadatok között van, és nem a Számlázás szekcióban. */}
            <div className="grid gap-5 sm:grid-cols-3">
              <TextField<AccountFormValues>
                name="zip"
                label={t('accounts.form.zip')}
                disabled={disabled}
                autoComplete="off"
              />
              <div className="sm:col-span-2">
                <TextField<AccountFormValues>
                  name="city"
                  label={t('accounts.form.city')}
                  disabled={disabled}
                  autoComplete="off"
                />
              </div>
            </div>
            <TextField<AccountFormValues>
              name="address"
              label={t('accounts.form.address')}
              disabled={disabled}
              autoComplete="off"
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField<AccountFormValues>
                name="countryCode"
                label={t('accounts.form.countryCode')}
                disabled={disabled}
                autoComplete="off"
              />
              <TextField<AccountFormValues>
                name="countryName"
                label={t('accounts.form.countryName')}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
            {/* KOORDINÁTÁK (legacy-paritás, v4): a cím alatt, mert ugyanazt a dolgot írják le. STRING mező,
                nem number — a `decimal(10,6)` stringként lossless (nincs float-kerekítés). A legacy
                `required_with` szabálya: a kettő KÖLCSÖNÖSEN kötelező, ha az egyik ki van töltve. */}
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField<AccountFormValues>
                name="latitude"
                label={t('accounts.form.latitude')}
                hint={t('accounts.form.coordinateHint')}
                disabled={disabled}
                autoComplete="off"
              />
              <TextField<AccountFormValues>
                name="longitude"
                label={t('accounts.form.longitude')}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
          </div>
        </FormSection>

        {/* EGYÉB ADATOK — a legacy „other_data": a fiókot kiszolgáló technikai rendszerek (PMS, foglalási
            motor, channel manager, hosting) + a domainek + a szabad jegyzet. */}
        <FormSection
          id={ACCOUNT_SECTION.other}
          title={t('accounts.sections.other')}
          description={t('accounts.sections.otherDesc')}
        >
          <div className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <SelectField<AccountFormValues>
                name="pms"
                label={t('accounts.fields.pms')}
                disabled={disabled}
                placeholder={t('accounts.form.none')}
                options={options(refs?.pms, v.pms)}
              />
              <SelectField<AccountFormValues>
                name="bookingEngine"
                label={t('accounts.fields.bookingEngine')}
                disabled={disabled}
                placeholder={t('accounts.form.none')}
                options={options(refs?.bookingEngine, v.bookingEngine)}
              />
              <SelectField<AccountFormValues>
                name="channelManager"
                label={t('accounts.fields.channelManager')}
                disabled={disabled}
                placeholder={t('accounts.form.none')}
                options={options(refs?.channelManager, v.channelManager)}
              />
              <SelectField<AccountFormValues>
                name="emailWebHosting"
                label={t('accounts.fields.emailWebHosting')}
                disabled={disabled}
                placeholder={t('accounts.form.none')}
                options={options(refs?.hosting, v.emailWebHosting)}
              />
            </div>
            {/* KULCS-LISTÁK (legacy-paritás, v4): a legacy JSON-tömb oszlopok (`export_campaigns`/`smtps`/
                `mailings`) — zárt értékkészlet, ezért reference-data-vezérelt több-választó, a `types`
                mintájára. A nem-vokabulált, de már kiválasztott értéket megőrizzük (a legacy slug ne tűnjön el). */}
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('accounts.form.smtps')}</label>
                <EntityMultiSelect
                  options={multiOptions(refs?.smtp, selectedSmtps)}
                  value={selectedSmtps}
                  onChange={(ids) => form.setValue('smtps', ids, { shouldDirty: true })}
                  placeholder={t('accounts.form.none')}
                  disabled={disabled}
                  aria-label={t('accounts.form.smtps')}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('accounts.form.mailings')}</label>
                <EntityMultiSelect
                  options={multiOptions(refs?.mailing, selectedMailings)}
                  value={selectedMailings}
                  onChange={(ids) => form.setValue('mailings', ids, { shouldDirty: true })}
                  placeholder={t('accounts.form.none')}
                  disabled={disabled}
                  aria-label={t('accounts.form.mailings')}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('accounts.form.exportCampaigns')}</label>
                <EntityMultiSelect
                  options={multiOptions(refs?.exportCampaign, selectedCampaigns)}
                  value={selectedCampaigns}
                  onChange={(ids) => form.setValue('exportCampaigns', ids, { shouldDirty: true })}
                  placeholder={t('accounts.form.none')}
                  disabled={disabled}
                  aria-label={t('accounts.form.exportCampaigns')}
                />
              </div>
            </div>
            {/* JELÖLŐ (legacy-paritás, v4): `cloudflare` = a fiók Cloudflare-en van-e. A globális
                `Checkbox`-szal, `label htmlFor`-ral kötve.
                ⚠️ A legacy `checked` mező SZÁNDÉKOSAN NINCS itt: az üzleti jelentése sehol nincs definiálva
                (`docs/modules/admin/accounts.md` „Furcsaságok"), és a kivezetést teszt-fal is rögzíti
                (`clients.controller.spec.ts` — „a fiók-válasz SEHOL nem hordozza"). Az ADAT viszont a v4
                sémában és az ETL-ben MEGVAN (301 élő fiókon) → ha kiderül a jelentése, egy kis lépés
                megjeleníteni; addig nem mutatunk olyan mezőt, amiről nem tudjuk, mit jelent. */}
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  id="account-cloudflare"
                  checked={form.watch('cloudflare')}
                  disabled={disabled}
                  onCheckedChange={(c) =>
                    form.setValue('cloudflare', c === true, { shouldDirty: true })
                  }
                />
                <label htmlFor="account-cloudflare">{t('accounts.form.cloudflare')}</label>
              </div>
            </div>
            <TextareaField<AccountFormValues>
              name="note"
              label={t('accounts.form.note')}
              disabled={disabled}
              rows={4}
            />
          </div>
        </FormSection>

        {/* DOMAINEK — a fiók nem-fő domainjei állapottal (a régi „Egyéb domainek" szöveg utódja). Ismételhető
            sorok a rendszerazonosítók mintájára — a lap EGYETLEN Mentés-gombja menti (nem oldalpanel). */}
        <FormSection id={ACCOUNT_SECTION.domains} title={t('accounts.domains.title')}>
          <AccountDomainsField form={form} disabled={disabled} />
        </FormSection>

        {/* SZOLGÁLTATÁSOK — read-only lista a form KÖZEPÉN (a kért szekció-sorrend miatt). */}
        {servicesSlot}

        {/* SZÁMLÁZÁS — a legacy „billing" tabja: a fiók számlázási profilját FELÜLÍRÓ választó + a fiók-szintű
            automatikus számlázás. A legacy `overrideBilling` KAPCSOLÓRA itt nincs szükség: a „— nincs —" opció
            MAGA a „ne írjuk felül" eset (ilyenkor az ügyfél elsődleges profilja számláz).
            ⚠️ KORREKCIÓ (2026-08-21, a Fiókok review-ján MÉRVE): itt korábban az állt, hogy „az `autoBilling` az
            ÜGYFÉLEN él" — ez PARITÁS-HIBA volt. A legacy-ben `accounts.auto_billing` KÜLÖN oszlop (379 élő
            fiókból 351-en be volt kapcsolva), és a havi számlázó az ACCOUNT-scope hozzárendeléseknél EZT
            olvassa (`auto_billing = true ÉS status = active`). Az ügyfél-szintű kapcsoló ettől független. */}
        <FormSection
          id={ACCOUNT_SECTION.billing}
          title={t('accounts.sections.billing')}
          description={t('accounts.sections.billingDesc')}
        >
          {/* SAJÁT SZÁMLÁZÁS (a legacy `override_billing`) — a profil-választó FÖLÖTT, mert ez a KAPUJA:
              kikapcsolva az ügyfél elsődleges profilja számláz, ÉS a fiók-szintű hozzárendelések az ÜGYFÉL
              számlájába olvadnak be. Bekapcsolva a fiók saját profillal, ÖNÁLLÓ számlát kap. A két hatás a
              legacy-ben ugyanezen a flagen ült (`getEffectiveBillingProfile` + a havi generátor csoportosítása). */}
          <div className="mb-5 flex items-center gap-2 text-sm">
            <Checkbox
              id="account-override-billing"
              checked={form.watch('overrideBilling')}
              disabled={disabled}
              onCheckedChange={(c) =>
                form.setValue('overrideBilling', c === true, { shouldDirty: true })
              }
            />
            <label htmlFor="account-override-billing" className="cursor-pointer select-none">
              {t('accounts.form.overrideBilling')}
            </label>
          </div>
          <Text variant="secondary" as="p" className="mb-5">
            {t('accounts.form.overrideBillingHint')}
          </Text>

          <div className="grid gap-5 sm:grid-cols-2">
            <SelectField<AccountFormValues>
              name="billingProfileId"
              label={t('accounts.form.billingProfile')}
              // A választó CSAK saját számlázásnál értelmezett: kikapcsolva az ügyfél elsődlegese számláz.
              disabled={billingProfileDisabled || !form.watch('overrideBilling')}
              placeholder={t('accounts.form.billingProfileNone')}
              hint={
                mode === 'create' && !parentChosen
                  ? t('accounts.form.billingProfileCreateHint')
                  : !form.watch('overrideBilling')
                    ? t('accounts.form.billingProfileDisabledHint')
                    : ''
              }
              options={billingProfileSelectOptions}
            />
          </div>
          <div className="mt-5 flex items-center gap-2 text-sm">
            <Checkbox
              id="account-auto-billing"
              checked={form.watch('autoBilling')}
              disabled={disabled || autoBillingBlocked}
              onCheckedChange={(c) =>
                form.setValue('autoBilling', c === true, { shouldDirty: true })
              }
            />
            <label htmlFor="account-auto-billing">{t('accounts.form.autoBilling')}</label>
          </div>
          <p className="text-muted-foreground mt-1.5 text-xs">
            {autoBillingBlocked
              ? t('accounts.form.autoBillingBlockedNoPrimary')
              : t('accounts.form.autoBillingHint')}
          </p>
          {autoBillingError && (
            <p role="alert" className="text-destructive mt-1 text-xs font-medium">
              {autoBillingError}
            </p>
          )}
        </FormSection>

        {/* INTEGRÁCIÓK — belső rendszer-azonosítók; CSAK írás-joggal (a view-only nézetnek nem mondanak semmit).
            Ez az UTOLSÓ írható szekció → a mentés-gomb is itt áll (lásd a fájl fejléc-kommentjét). */}
        {!disabled && (
          <FormSection
            id={ACCOUNT_SECTION.integrations}
            title={t('accounts.sections.integrations')}
            description={t('accounts.sections.integrationsDesc')}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField<AccountFormValues>
                name="asanaName"
                label={t('accounts.fields.asanaName')}
                disabled={disabled}
                autoComplete="off"
              />
            </div>

            {/* A külső rendszer-azonosítók — rendszerenként több is, megjegyzéssel (a hozzárendelés ezekre köt). */}
            <AccountIntegrationsField form={form} disabled={disabled} />

            {/* AKCIÓ — JOBBRA (a sáv-/lábléc-szabály), mobilon teljes szélességű, hogy a hüvelykujj-zónába
                essen. „Vissza" gomb NINCS: a vissza-navigáció helye a topbar szülő-morzsája („Fiókok / …"). */}
            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                variant="brand"
                disabled={submitDisabled}
                className="max-sm:w-full"
              >
                {pending
                  ? t('accounts.form.saving')
                  : mode === 'create'
                    ? t('accounts.form.create')
                    : t('accounts.form.save')}
              </Button>
            </div>
          </FormSection>
        )}

        {children}
      </form>
    </Form>
  );
}
